import { BEIGE_600, BOX_SHADOW_XS, CORAL_200, CORAL_600, GRAY_600, GRAY_700, GRAY_900, GREEN_200, GREEN_600 } from 'constants/styling/theme'
import { Box, Divider, Stack, Typography } from '@mui/material'
import { DetailCommentBlock, DetailField, DetailSection } from 'components/common/Gallery/AssignmentDetailDrawer/common'
import { GALLERY_DRAWER_HEADER_SX, ResponsiveDrawer } from 'components/common/ResponsiveDrawer'
import React, { useCallback, useMemo, useState } from 'react'
import { Trans, useTranslation } from 'react-i18next'

import { BillingMethod } from 'constants/deal'
import { Creative } from 'models/creative'
import { EventTime } from 'components/common/EventTime'
import { LinearProgressBar } from 'components/common/LinearProgressBar'
import { MUIBadge } from 'components/common/MUIBadge'
import { MUIButton } from 'components/common/MUIButton'
import { NameAvatar } from 'components/common/Avatars'
import { OrderCancellationPopup } from 'components/common/OrderCancellationPopup'
import { ProductDTOClient } from 'models/product'
import { SectionContentList } from '../common/SectionContentList'
import { ShootingCategory } from 'constants/product'
import { choosePrimaryProduct } from 'utils/product'
import { compareByShootingStart } from 'utils/time'
import { getMeetingTypeProduct } from 'utils/helpers'
import { getTermsLink } from 'utils/localization'
import i18n from 'translations/i18n'
import moment from 'moment-timezone'
import { useDynamicNow } from 'utils/hooks'
import { useGalleryDeal } from '../../contexts'
import { useGalleryProduct } from '../../contexts/GalleryProduct.context'
import { useTimezone } from 'components/contexts/timezone.context'

/** Billing method display label mapping */
const BILLING_METHOD_LABELS: Record<BillingMethod, string> = {
  [BillingMethod.CARD]: 'gallery:order_detail.billing.method_card',
  [BillingMethod.SEPA_DEBIT]: 'gallery:order_detail.billing.method_sepa',
  [BillingMethod.INVOICE]: 'gallery:order_detail.billing.method_invoice',
}

export interface Props {
  /** Whether the order detail drawer is open or not */
  isOpen: boolean,
  /** OnClick action to close the drawer */
  onClose: () => void
}

/**
 * @component Client Gallery Order detail drawer
 * @example <ClientGalleryOrderDetailDrawer isOpen={true} onClose={onClose} />
 */
export const ClientGalleryOrderDetailDrawer: React.FC<Props> = ({
  isOpen,
  onClose
}) => {
  const { t } = useTranslation(['gallery', 'actions', 'product', 'product_kind', 'category', 'order_cancallation_popup'])
  const { dealData, dealAssignmentsForTab, allowCancelOrder, shootingStart, earliestAssignment } = useGalleryDeal()
  const { isDocumentProduct } = useGalleryProduct()
  const { userTimezone } = useTimezone()
  const dynamicNow = useDynamicNow()

  /** Hide the cancellation shooting information for document products */
  const hideCancellationShootingInfo = useMemo(() => isDocumentProduct, [isDocumentProduct])

  /** Whether cancellation is blocked only because shooting has started (order is otherwise cancellable) */
  const isCancellationPastShootingStart = useMemo(() => {
    return !!shootingStart && dynamicNow.isSameOrAfter(shootingStart) && !isDocumentProduct
  }, [shootingStart, dynamicNow, isDocumentProduct])

  const [isCancellationPopupOpen, setIsCancellationPopupOpen] = useState(false)

  const na = '-'

  const productTitle = useCallback((product: ProductDTOClient) => {
    return (
      t(`product:p_${product.id}`,
        t(`product_kind:${product.kind}`,
          t(`category:${product.category}`)
        )
      )
    )
  }, [t])

  const meetingTypeProduct = useMemo(
    () => getMeetingTypeProduct(dealData?.financials.products, dealAssignmentsForTab),
    [dealAssignmentsForTab, dealData?.financials.products]
  )

  const sortedDealAssignmentsForTab = useMemo(
    () => [...dealAssignmentsForTab].sort(compareByShootingStart),
    [dealAssignmentsForTab]
  )

  const billingMethodLabel = useMemo(() => {
    if (!dealData?.billingMethod) return undefined
    const key = BILLING_METHOD_LABELS[dealData.billingMethod]
    return key ? t(key) : undefined
  }, [dealData?.billingMethod, t])

  const allAssignmentCreatives = useMemo(() => {
    if (!dealData?.assignments) return []
    const creatives = dealData.assignments.map((a) => a.creative).filter((c): c is Creative => !!c)

    return [...new Map(creatives.map((c) => [c.creativeId, c])).values()]
  }, [dealData?.assignments])

  const hasShootingAssignment = useMemo(
    () => dealAssignmentsForTab.some((assignment) => assignment.products.some((product) => product.category === ShootingCategory.REAL_ESTATE)),
    [dealAssignmentsForTab]
  )

  const isLateCancellation = useMemo(() => {
    if (!dealData || !shootingStart || !earliestAssignment?.timezone) return false
    return moment(shootingStart).isSameOrBefore(moment(dynamicNow).add(1, 'day'))
  }, [shootingStart, dealData, dynamicNow, earliestAssignment])

  const lastCancelDateTimeWithoutFee = useMemo(() => moment(shootingStart).subtract(1, 'day'), [shootingStart])
  const orderCreatedAt = useMemo(() => dealData?.createdAt ? moment(dealData.createdAt).toISOString() : null, [dealData])

  const progressWithoutFee = useMemo(() => {
    if (!shootingStart || !orderCreatedAt) return 0
    if (isLateCancellation) return 100
    const totalValue = lastCancelDateTimeWithoutFee.diff(moment(orderCreatedAt))
    const currentValue = moment(dynamicNow).diff(moment(orderCreatedAt))
    return (currentValue / totalValue) * 100
  }, [dynamicNow, isLateCancellation, lastCancelDateTimeWithoutFee, orderCreatedAt, shootingStart])

  const progressWithFee = useMemo(() => {
    if (!shootingStart) return 0
    if (!isLateCancellation) return 0
    if (moment(shootingStart).isSameOrBefore(moment(dynamicNow))) return 100
    const totalValue = moment(shootingStart).diff(moment(lastCancelDateTimeWithoutFee))
    const currentValue = moment(dynamicNow).diff(moment(lastCancelDateTimeWithoutFee))
    return (currentValue / totalValue) * 100
  }, [dynamicNow, isLateCancellation, lastCancelDateTimeWithoutFee, shootingStart])

  if (!dealData) return <></>

  return (
    <ResponsiveDrawer
      backdrop="blurred"
      isOpen={isOpen}
      onClose={onClose}
      title={
        <Stack direction="row" alignItems="center" gap=".6rem">

          {t('order_informations.order')}
          <Box component="span" sx={{ width: '.4rem', height: '.4rem', borderRadius: '50%', backgroundColor: GRAY_600, flexShrink: 0 }} />
          <Box component="span" color={GRAY_700}>#{dealData.id}</Box>

        </Stack>}

      headerSx={GALLERY_DRAWER_HEADER_SX}
      actionsSlot={
        <Stack
          flexDirection="column"
          justifyContent="flex-end"
        >
          <MUIButton
            type='secondaryBorder'
            borderStyle='rounded'
            onClick={onClose}
          >
            {t('Close')}
          </MUIButton>
        </Stack>
      }
    >

      {!!dealData.createdAt &&
        <DetailField label={t('gallery:order_detail.created_at')}>
          <Typography variant="text-md" fontWeight={350} color={GRAY_900}>
            {moment(dealData.createdAt).locale(i18n.language).format('MMMM Do YYYY, h:mm A')}
          </Typography>
        </DetailField>
      }

      {/** CANCEL ORDER SECTION */}
      {(allowCancelOrder || isCancellationPastShootingStart) &&
        <Stack gap=".4rem" paddingBottom="1.2rem">
          <Typography variant="text-lg" fontWeight={550} color={GRAY_900}>
            {t('gallery:order_detail.cancel.title')}
          </Typography>

          {!hideCancellationShootingInfo &&
            <Stack gap=".2rem">
              <Stack direction="row" gap=".4rem" width="100%" alignItems="center">
                <Box flexGrow={1} padding=".2rem 0">
                  <LinearProgressBar value={progressWithoutFee} barBackgroundColor={GREEN_200} barColor={GREEN_600} />
                </Box>

                <MUIBadge label="24h" size="sm" color={isLateCancellation ? 'coral' : 'gray'} />

                <Box flexGrow={1} maxWidth="10rem" padding=".2rem 0">
                  <LinearProgressBar value={progressWithFee} barBackgroundColor={CORAL_200} barColor={CORAL_600} />
                </Box>
              </Stack>

              {!allowCancelOrder &&
                <Typography variant="text-sm" color={CORAL_600}>
                  {t('order_cancallation_popup:cancel_order_not_possible')}
                </Typography>
              }
            </Stack>
          }

          <Stack direction="row" gap="2.4rem" alignItems="center" justifyContent="space-between" marginTop=".8rem">
            <Typography variant="text-xs" color={GRAY_700} flex={1}>
              <Trans
                t={t}
                ns="order_cancallation_popup"
                i18nKey="cancel_terms_and_condition"
              >
                <a
                  href={getTermsLink(i18n.language)}
                  target="_blank"
                  rel="noreferrer"
                  style={{ textDecoration: 'none', fontWeight: 550 }}
                >
                </a>
              </Trans>
            </Typography>

            <MUIButton
              type="defaultPrimary"
              disabled={!allowCancelOrder}
              onClick={() => setIsCancellationPopupOpen(true)}
              sx={{ whiteSpace: 'nowrap' }}
            >
              {t('gallery:order_detail.cancel.cancel_button')}
            </MUIButton>
          </Stack>
        </Stack>
      }

      {(allowCancelOrder || isCancellationPastShootingStart) &&
        <Divider sx={{ marginBottom: '1.2rem', marginLeft: '-2rem', marginRight: '-2rem' }} />
      }

      {/** SCHEDULE */}
      {hasShootingAssignment &&
        <DetailSection title={t('gallery:order_detail.schedule.title')}>
          <SectionContentList title={t('gallery:order_detail.schedule.meeting')}>
            <Box fontSize="1.6rem" fontWeight={350} lineHeight="2.4rem" color={GRAY_900}>
              {meetingTypeProduct ? t(`product_kind:${meetingTypeProduct.kind}`) : na}
            </Box>
          </SectionContentList>

          {!!dealData.contactPerson && (!!dealData.contactPerson.name || !!dealData.contactPerson.email || !!dealData.contactPerson.phone) &&
            <SectionContentList title={t('gallery:order_detail.schedule.contact_person')}>
              {!!dealData.contactPerson.name &&
                <Typography variant="text-md" fontWeight={350} lineHeight="2rem" color={GRAY_900}>
                  {dealData.contactPerson.name}
                </Typography>
              }
              <Stack height="2rem" flexDirection="row" alignItems="center" gap=".4rem">
                <Typography color={GRAY_700} fontWeight={500}>{t('gallery:order_detail.schedule.email')}</Typography>
                <Typography variant="text-md" fontWeight={350} color={GRAY_900}>{dealData.contactPerson.email || na}</Typography>
              </Stack>
              <Stack height="2rem" flexDirection="row" alignItems="center" gap=".4rem">
                <Typography color={GRAY_700} fontWeight={500}>{t('gallery:order_detail.schedule.phone')}</Typography>
                <Typography variant="text-md" fontWeight={350} color={GRAY_900}>{dealData.contactPerson.phone || na}</Typography>
              </Stack>
            </SectionContentList>
          }

          <SectionContentList title={t('gallery:order_detail.schedule.date_and_time')}>
            <Stack gap={1.2}>
              {sortedDealAssignmentsForTab.map((assignment) => {
                const primaryProduct = choosePrimaryProduct(assignment.products)

                return (
                  <Box key={assignment.id}>

                    <Typography variant="text-md" fontWeight={450} lineHeight="2rem" color={GRAY_900} paddingRight={2}>
                      {primaryProduct && productTitle(primaryProduct)}
                    </Typography>

                    <Stack direction="row" alignItems="center" paddingLeft="1rem" paddingTop=".4rem">
                      {!!assignment.shootingStartDateTime &&
                        <EventTime
                          eventStart={assignment.shootingStartDateTime}
                          eventTimezone={assignment.timezone}
                          userTimezone={userTimezone}
                          minDuration={assignment.timeOnSiteMin}
                          maxDuration={assignment.timeOnSiteMax}
                        />
                      }
                    </Stack>

                  </Box>
                )
              })}
            </Stack>
          </SectionContentList>

          <SectionContentList title={t('gallery:order_detail.schedule.meeting_instructions')}>
            <Box marginTop=".2rem">
              <DetailCommentBlock>
                {dealData.meetingInstructions || na}
              </DetailCommentBlock>
            </Box>
          </SectionContentList>

        </DetailSection>
      }

      {/** SELECTED CREATIVE */}
      {hasShootingAssignment && !dealData.favouriteCreative &&
        <DetailSection title={t('gallery:order_detail.selected_creative.title')}>
          {allAssignmentCreatives.map((creative) =>
            <Stack key={creative.creativeId} direction="row" alignItems="center" gap=".8rem">
              <NameAvatar
                name={creative.name}
                size="3.2rem"
                textVariant="text-xs"
                elevation="xs"
              />
              <Typography variant="text-md" fontWeight={450}>
                {creative.name}
              </Typography>
            </Stack>
          )}
          {!allAssignmentCreatives.length &&
            <Typography variant="text-md" fontWeight={350} color={GRAY_900}>
              {t('gallery:order_detail.selected_creative.not_selected')}
            </Typography>
          }
        </DetailSection>
      }

      {/** FAVOURITE CREATIVE (only when the client selected one during ordering) */}
      {!!dealData.favouriteCreative &&
        <DetailSection title={t('gallery:order_detail.favourite_creative.title')}>
          <Stack direction="row" alignItems="center" gap=".8rem">
            <NameAvatar
              name={dealData.favouriteCreative.name}
              size="3.2rem"
              textVariant="text-xs"
              elevation="xs"
            />
            <Typography variant="text-md" fontWeight={450}>
              {dealData.favouriteCreative.name}
            </Typography>
          </Stack>
        </DetailSection>
      }

      {/** OVERALL INFORMATIONS */}
      <DetailSection title={t('gallery:order_detail.overall.title')}>
        <DetailField label={t('gallery:order_detail.overall.reference')}>
          <Box fontWeight={350} color={GRAY_900}>{dealData.reference || na}</Box>
        </DetailField>

        <DetailField label={t('gallery:order_detail.overall.comments')}>
          <DetailCommentBlock>
            {dealData.shootingInstructions || na}
          </DetailCommentBlock>
        </DetailField>

        <DetailField label={t('gallery:order_detail.overall.cc_emails')}>
          {!!dealData.ccEmails?.length &&
            <Stack gap=".8rem">
              {dealData.ccEmails.map((email) =>
                <Stack key={email} direction="row" alignItems="center" gap=".8rem">
                  <NameAvatar
                    name={email}
                    size="3.2rem"
                    textVariant="text-xs"
                    elevation="none"
                  />
                  <Box fontWeight={350} lineHeight="2.4rem" color={GRAY_900}>
                    {email}
                  </Box>
                </Stack>
              )}
            </Stack>
          }
          {!dealData.ccEmails?.length &&
            <Box fontWeight={350} lineHeight="2.4rem" color={GRAY_900}>{na}</Box>
          }
        </DetailField>
      </DetailSection>

      {/** BILLING INFORMATIONS */}
      <DetailSection title={t('gallery:order_detail.billing.title')}>
        <DetailField label={t('gallery:order_detail.billing.billing_reference')}>
          <Box fontWeight={350} color={GRAY_900}>{dealData.reference || na}</Box>
        </DetailField>
        <DetailField label={t('gallery:order_detail.billing.payment_method')}>
          <Box fontWeight={350} color={GRAY_900}>{billingMethodLabel || na}</Box>
        </DetailField>

        <Box paddingBottom="1.6rem">
          <Box
            fontSize="1.4rem"
            fontWeight={500}
            color={GRAY_700}
            marginBottom=".8rem"
            lineHeight="2rem"
          >
            {t('gallery:order_detail.billing.billing_address')}
          </Box>

          {!!dealData.invoicingAddress &&
            <Box
              border={`1px solid ${BEIGE_600}`}
              borderRadius=".8rem"
              overflow="hidden"
              boxShadow={BOX_SHADOW_XS}
            >
              <Box
                padding="1.6rem"
                borderBottom={`1px solid ${BEIGE_600}`}
                fontSize="1.6rem"
                fontWeight={600}
                lineHeight="2.4rem"
                color={GRAY_900}
              >
                {dealData.invoicingAddress.companyOrIndividualName}
              </Box>
              <Stack padding="1.6rem" gap=".2rem" fontSize="1.4rem" lineHeight="2rem">
                <Box color={GRAY_900}>{dealData.invoicingAddress.street}</Box>
                <Box color={GRAY_700}>
                  {dealData.invoicingAddress.postalCode}, {dealData.invoicingAddress.city}, {dealData.invoicingAddress.countryCode}
                </Box>
                {!!dealData.invoicingAddress.vatNumber &&
                  <Box color={GRAY_700}>{dealData.invoicingAddress.vatNumber}</Box>
                }
              </Stack>
            </Box>
          }

          {!dealData.invoicingAddress &&
            <Box
              border={`1px solid ${BEIGE_600}`}
              borderRadius=".8rem"
              overflow="hidden"
              boxShadow={BOX_SHADOW_XS}
            >
              <Box
                padding="1.6rem"
                borderBottom={`1px solid ${BEIGE_600}`}
                fontSize="1.6rem"
                fontWeight={600}
                lineHeight="2.4rem"
                color={GRAY_900}
              >
                {dealData.property?.title || na}
              </Box>
              <Stack padding="1.6rem" gap=".2rem" fontSize="1.4rem" lineHeight="2rem">
                {!!dealData.property?.address &&
                  <>
                    <Box color={GRAY_900}>
                      {dealData.property.address.street || na}
                    </Box>
                    <Box color={GRAY_700}>
                      {[dealData.property.address.postalCode, dealData.property.address.city, dealData.property.address.country].filter(Boolean).join(', ') || na}
                    </Box>
                  </>
                }
                {!dealData.property?.address &&
                  <Box color={GRAY_700}>{na}</Box>
                }
              </Stack>
            </Box>
          }
        </Box>
      </DetailSection>

      {/** CANCELLATION POPUP */}
      {!!dealData &&
        <OrderCancellationPopup
          data={dealData}
          isOpen={isCancellationPopupOpen}
          dynamicNow={dynamicNow}
          onClickClose={() => setIsCancellationPopupOpen(false)}
        />
      }

    </ResponsiveDrawer>
  )
}
